import React, { Fragment, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GET, TenantID } from "../dataSaver";
import Navbar from "../../Components/Navbar";
import SideBar from "../../Components/Sidebar";
import style from "./index.module.scss";
import { Icon, Intent } from "@blueprintjs/core";
import { Link } from "react-router-dom";
import LevelTwoHeader from '../../Components/LevelTwoHeader';
import AddSiteSelectionPopup from "./AddSiteSelectionPopup";
import { ErrorToaster } from "../../utils/toaster";
import { format } from 'date-fns';
import TimesheetAlerts from './TimesheetAlerts';
import TimesheetAlertsTable from './TimesheetAlertsTable';

const AlertsNotifications = () => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [showSitePopup, setShowSitePopup] = useState(false);
    const isMultiSiteEntity = sessionStorage.getItem("isMultiSiteEntity") === "true";
    const [alerts, setAlerts] = useState({
        TIMESHEET_CREATION_REMINDER: {
            count: 3,
            isActivated: true,
            lastUpdated: null,
            hasData: false
        },
        TIMESHEET_SUBMISSION_REMINDERS: {
            count: 3,
            isActivated: true,
            lastUpdated: null,
            hasData: false
        },
        TIMESHEET_REVIEW_APPROVE_REMINDER: {
            count: 3,
            isActivated: true,
            lastUpdated: null,
            hasData: false
        },
        TIMESHEET_REJECTION_DISPUTE_REMINDER: {
            count: 3,
            isActivated: true,
            lastUpdated: null,
            hasData: false
        }
    });
    const [showModal, setShowModal] = useState(false);
    const [selectedAlertType, setSelectedAlertType] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const [mostRecentUpdate, setMostRecentUpdate] = useState(null);
    const [contentType, setContentType] = useState('main'); // 'main', 'alerts', 'alertsTable'
    const [alertData, setAlertData] = useState(null);
    const [selectedSite, setSelectedSite] = useState(null);

    // Get URL parameters
    const searchParams = new URLSearchParams(location.search);
    const reminderType = searchParams.get('reminderType') || location.state?.reminderType || 'TIMESHEET_CREATION_REMINDER';
    const view = searchParams.get('view');

    // Set content type based on URL parameters
    useEffect(() => {
        if (view === 'alerts') {
            setContentType('alerts');
        } else if (view === 'alertsTable' || location.state?.view === 'alertsTable') {
            setContentType('alertsTable');
        } else {
            setContentType('main');
        }

        // Handle location state for selected site and alert data
        if (location.state?.selectedSite) {
            setSelectedSite(location.state.selectedSite);
        }

        if (location.state?.alertData) {
            setAlertData(location.state.alertData);
        }
    }, [location.search, location.state]);

    const checkExistingAlerts = async (reminderType) => {
        try {
            const { data } = await GET(
                `timesheet-management-service/reminder?reminderType=${reminderType}&tenantId=${TenantID}&offset=0&limit=100`
            );

            return data?.reminderData?.length > 0;
        } catch (error) {
            console.error('Error checking alerts:', error);
            return false;
        }
    };

    const handleAlertClick = (reminderType) => async (e) => {
        e.preventDefault();
        console.log('Selected reminder type:', reminderType);
        setSelectedAlertType(reminderType);

        if (isMultiSiteEntity) {
            const hasExistingAlerts = await checkExistingAlerts(reminderType);

            if (hasExistingAlerts) {
                // Check if we have site data in the response
                const { data } = await GET(
                    `timesheet-management-service/reminder?reminderType=${reminderType}&tenantId=${TenantID}&offset=0&limit=100`
                );

                const hasSiteData = data?.reminderData?.some(reminder => reminder.site?.id);

                if (hasSiteData) {
                    // If both alerts and site data exist, go to alertsTable view
                    navigate(`/alerts-notifications?view=alertsTable&reminderType=${reminderType}`);
                } else {
                    // If no site data, show site selection popup
                    setShowSitePopup(true);
                }
            } else {
                // If no alerts exist, show site selection popup
                setShowSitePopup(true);
            }
        } else {
            // For non-multi-site entities, go directly to alerts setup
            navigate(`/alerts-notifications?view=alerts&reminderType=${reminderType}`);
        }
    };

    const handleSiteSelection = ({ selectedSite, reminderType, showTimesheetModal }) => {
        setShowSitePopup(false);
        setSelectedSite(selectedSite);

        if (showTimesheetModal) {
            // Show the TimesheetAlerts modal
            navigate(`/alerts-notifications?view=alerts&reminderType=${reminderType}`, {
                state: {
                    selectedSite: selectedSite,
                    reminderType: reminderType,
                    showTimesheetModal: true
                }
            });
        } else {
            // Default navigation
            navigate(`/alerts-notifications?view=alerts&reminderType=${selectedAlertType}`, {
                state: { selectedSite: selectedSite, reminderType: selectedAlertType }
            });
        }
    };

    const getIsExpanded = (value) => {
        setIsExpanded(value);
    };

    const [buttonClasses, setButtonClasses] = useState({
        TIMESHEET_CREATION_REMINDER: style.setupButton,
        TIMESHEET_SUBMISSION_REMINDERS: style.setupButton,
        TIMESHEET_REVIEW_APPROVE_REMINDER: style.setupButton,
        TIMESHEET_REJECTION_DISPUTE_REMINDER: style.setupButton
    });

    const fetchLastModifiedDates = async () => {
        try {
            const reminderTypes = [
                'TIMESHEET_CREATION_REMINDER',
                'TIMESHEET_SUBMISSION_REMINDERS',
                'TIMESHEET_REVIEW_APPROVE_REMINDER',
                'TIMESHEET_REJECTION_DISPUTE_REMINDER'
            ];

            const updatedAlerts = { ...alerts };
            let allDates = [];

            for (const reminderType of reminderTypes) {
                try {
                    const { data } = await GET(
                        `timesheet-management-service/reminder?reminderType=${reminderType}&tenantId=${TenantID}&offset=0&limit=100`
                    );

                    if (data?.reminderData?.length > 0) {
                        const lastModifiedDates = data.reminderData
                            .map(reminder => reminder.lastModifiedDate)
                            .filter(date => date);

                        // Add valid dates to allDates array
                        allDates = [...allDates, ...lastModifiedDates];

                        if (lastModifiedDates.length > 0) {
                            const mostRecentDate = new Date(Math.max(...lastModifiedDates.map(date => new Date(date))));
                            updatedAlerts[reminderType] = {
                                ...updatedAlerts[reminderType],
                                lastUpdated: format(mostRecentDate, 'MMMM yyyy').toUpperCase(),
                                hasData: true
                            };
                        }
                    }
                } catch (error) {
                    console.error(`Error fetching data for ${reminderType}:`, error);
                }
            }

            // Update most recent date for header
            if (allDates.length > 0) {
                const mostRecentOverall = new Date(Math.max(...allDates.map(date => new Date(date))));
                setMostRecentUpdate(format(mostRecentOverall, 'MMM dd, yyyy HH:mm'));
            } else {
                setMostRecentUpdate(null);
            }

            setAlerts(updatedAlerts);
        } catch (error) {
            console.error('Error in fetchLastModifiedDates:', error);
            ErrorToaster('Error fetching update dates');
        }
    };

    // Update button classes based on data existence
    const updateButtonClasses = async () => {
        const newButtonClasses = { ...buttonClasses };

        for (const reminderType of Object.keys(alerts)) {
            try {
                const { data } = await GET(
                    `timesheet-management-service/reminder?reminderType=${reminderType}&tenantId=${TenantID}&offset=0&limit=100`
                );

                newButtonClasses[reminderType] = data?.reminderData?.length > 0 ?
                    style.activeSetup : style.setupButton;
            } catch (error) {
                console.error(`Error checking button class for ${reminderType}:`, error);
                newButtonClasses[reminderType] = style.setupButton;
            }
        }

        setButtonClasses(newButtonClasses);
    };

    useEffect(() => {
        if (contentType === 'main') {
            fetchLastModifiedDates();
            updateButtonClasses();
        }
    }, [contentType]);

    // Get header title based on current content and reminder type
    const getHeaderTitle = () => {
        if (contentType === 'main') {
            return 'ALERTS & NOTIFICATIONS SETUP';
        }

        switch (reminderType) {
            case 'TIMESHEET_CREATION_REMINDER':
                return 'TIMESHEET CREATION ALERTS';
            case 'TIMESHEET_SUBMISSION_REMINDERS':
                return 'CREATED TIMESHEET SUBMISSION REMINDERS';
            case 'TIMESHEET_REVIEW_APPROVE_REMINDER':
                return 'TIMESHEET / INVOICE REVIEW & APPROVAL ALERTS';
            case 'TIMESHEET_REJECTION_DISPUTE_REMINDER':
                return 'TIMESHEET REJECTION / DISPUTE ALERTS';
            default:
                return 'TIMESHEET ALERTS';
        }
    };

    // Get back link based on current content
    const getBackLink = () => {
        if (contentType === 'alerts') {
            if (isMultiSiteEntity) {
                return `/alerts-notifications?view=alertsTable&reminderType=${reminderType}`;
            }
            return '/alerts-notifications';
        } else if (contentType === 'alertsTable') {
            return '/alerts-notifications';
        }
        return '/entitySitePortal';
    };

    // Handle back button click
    const handleBack = () => {
        navigate(getBackLink());
    };

    // Handle closing alerts setup modal
    const handleCloseAlerts = () => {
        if (isMultiSiteEntity) {
            navigate(`/alerts-notifications?view=alertsTable&reminderType=${reminderType}`);
        } else {
            navigate('/alerts-notifications');
        }
    };

    return (
        <Fragment>
            <Navbar />
            <div className={`${isExpanded ? style.bigCardGrid : style.smallCardGrid} ${style.margin20}`}>
                <div>
                    <SideBar isExpanded={isExpanded} getIsExpanded={getIsExpanded} />
                </div>
                <div className={style.mainContent}>
                    <LevelTwoHeader
                        heading={getHeaderTitle()}
                        updatedTime={mostRecentUpdate ? `UPDATED ON ${mostRecentUpdate} EST` : ''}
                        path={getBackLink()}
                        callingFrom={"Customer Admin"}
                        needHeader={true}
                        hideCountrySelector={true}
                    />

                    {contentType === 'main' && (
                        <div className={style.contentWrapper}>
                            <div className={style.alertsGrid}>
                                <Link
                                    to="#"
                                    onClick={handleAlertClick('TIMESHEET_CREATION_REMINDER')}
                                >
                                    <div className={style.alertCard}>
                                        <div className={`${style.alertContent}`}>
                                            <h3>TIMESHEET CREATION ALERTS</h3>
                                            {alerts.TIMESHEET_CREATION_REMINDER.hasData && (
                                                <div className={style.lastUpdated1}>
                                                    LAST UPDATED ON {alerts.TIMESHEET_CREATION_REMINDER.lastUpdated}
                                                </div>
                                            )}
                                            <button className={buttonClasses['TIMESHEET_CREATION_REMINDER']}>
                                                SETUP ALERTS
                                            </button>
                                        </div>
                                        <div className={style.status}>
                                            <span className={style.count}>{alerts.TIMESHEET_CREATION_REMINDER.count}</span>
                                            <span className={style.activated}>ACTIVATED</span>
                                        </div>
                                    </div>
                                </Link>

                                <Link
                                    to="#"
                                    onClick={handleAlertClick('TIMESHEET_SUBMISSION_REMINDERS')}
                                >
                                    <div className={style.alertCard}>
                                        <div className={style.alertContent}>
                                            <h3>CREATED TIMESHEET SUBMISSION REMINDERS</h3>
                                            {alerts.TIMESHEET_SUBMISSION_REMINDERS.hasData && (
                                                <div className={style.lastUpdated2}>
                                                    LAST UPDATED ON {alerts.TIMESHEET_SUBMISSION_REMINDERS.lastUpdated}
                                                </div>
                                            )}
                                            <button className={buttonClasses['TIMESHEET_SUBMISSION_REMINDERS']}>
                                                SETUP ALERTS
                                            </button>
                                        </div>
                                        <div className={style.status}>
                                            <span className={style.count}>{alerts.TIMESHEET_SUBMISSION_REMINDERS.count}</span>
                                            <span className={style.activated}>ACTIVATED</span>
                                        </div>
                                    </div>
                                </Link>

                                <Link
                                    to="#"
                                    onClick={handleAlertClick('TIMESHEET_REVIEW_APPROVE_REMINDER')}
                                >
                                    <div className={style.alertCard}>
                                        <div className={style.alertContent}>
                                            <h3>TIMESHEET / INVOICE REVIEW & APPROVAL ALERTS</h3>
                                            {alerts.TIMESHEET_REVIEW_APPROVE_REMINDER.hasData && (
                                                <div className={style.lastUpdated3}>
                                                    LAST UPDATED ON {alerts.TIMESHEET_REVIEW_APPROVE_REMINDER.lastUpdated}
                                                </div>
                                            )}
                                            <button className={buttonClasses['TIMESHEET_REVIEW_APPROVE_REMINDER']}>
                                                SETUP ALERTS
                                            </button>
                                        </div>
                                        <div className={style.status}>
                                            <span className={style.count}>{alerts.TIMESHEET_REVIEW_APPROVE_REMINDER.count}</span>
                                            <span className={style.activated}>ACTIVATED</span>
                                        </div>
                                    </div>
                                </Link>

                                <Link
                                    to="#"
                                    onClick={handleAlertClick('TIMESHEET_REJECTION_DISPUTE_REMINDER')}
                                >
                                    <div className={style.alertCard}>
                                        <div className={style.alertContent}>
                                            <h3>TIMESHEET REJECTION / DISPUTE ALERTS</h3>
                                            {alerts.TIMESHEET_REJECTION_DISPUTE_REMINDER.hasData && (
                                                <div className={style.lastUpdated4}>
                                                    LAST UPDATED ON {alerts.TIMESHEET_REJECTION_DISPUTE_REMINDER.lastUpdated}
                                                </div>
                                            )}
                                            <button className={buttonClasses['TIMESHEET_REJECTION_DISPUTE_REMINDER']}>
                                                SETUP ALERTS
                                            </button>
                                        </div>
                                        <div className={style.status}>
                                            <span className={style.count}>{alerts.TIMESHEET_REJECTION_DISPUTE_REMINDER.count}</span>
                                            <span className={style.activated}>ACTIVATED</span>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        </div>
                    )}

                    {contentType === 'alerts' && (
                        <div className={style.modalContent}>
                            <TimesheetAlerts
                                edit={location.state?.edit || false}
                                alertData={location.state?.alertData || null}
                                setOpenAlert={handleCloseAlerts}
                                selectedSite={selectedSite}
                                reminderType={reminderType}
                                inModal={true}
                            />
                        </div>
                    )}

                    {contentType === 'alertsTable' && (
                        <div className={style.modalContent}>
                            <TimesheetAlertsTable
                                reminderType={reminderType}
                                inModal={true}
                            />
                        </div>
                    )}
                </div>
            </div>
            {showSitePopup && (
                <AddSiteSelectionPopup
                    show={showSitePopup}
                    handleClose={() => setShowSitePopup(false)}
                    handleSelectedSite={handleSiteSelection}
                    reminderType={selectedAlertType}
                />
            )}
        </Fragment>
    );
};

export default AlertsNotifications; 